<template>
  <div>
    <div class="container">
      <div class="box-title">
        <h4>
          <i class="fal fa-calendar-star"></i>
          ວັນພັກ (Holidays)
          <span class="countTotal">({{ holidays.length }})</span>
        </h4>
      </div>

      <!-- Calendar -->
      <div class="box">
        <div class="calendar-header">
          <vs-button flat icon @click="prevMonth">
            <i class="fal fa-chevron-left"></i>
          </vs-button>
          <div class="calendar-selectors">
            <div class="select">
              <select v-model="calendarMonth">
                <option v-for="(m, i) in monthNames" :key="i" :value="i">{{ m }}</option>
              </select>
            </div>
            <div class="select">
              <select v-model="calendarYear">
                <option v-for="y in yearOptions" :key="y" :value="y">{{ y }}</option>
              </select>
            </div>
          </div>
          <vs-button flat icon @click="nextMonth">
            <i class="fal fa-chevron-right"></i>
          </vs-button>
        </div>

        <div class="calendar-grid">
          <div class="calendar-day-header" v-for="d in dayHeaders" :key="d">{{ d }}</div>
          <div
            v-for="(cell, i) in calendarCells"
            :key="i"
            :class="cellClass(cell)"
            @click="onCellClick(cell)"
          >
            <span v-if="cell.day" class="day-number">{{ cell.day }}</span>
            <span v-if="cell.holiday" class="holiday-dot" :title="cell.holiday.name"></span>
          </div>
        </div>

        <div class="calendar-legend">
          <span class="legend-item"><span class="holiday-dot"></span> ວັນພັກ</span>
          <span class="legend-item"><span class="weekend-dot"></span> ວັນເສົາ-ອາທິດ</span>
        </div>
      </div>

      <!-- Add / Edit Form -->
      <div v-if="showForm" class="box form-box">
        <h5 class="form-title">
          <i :class="editingId ? 'fal fa-edit' : 'fal fa-plus'"></i>
          {{ editingId ? 'ແກ້ໄຂວັນພັກ' : 'ເພີ່ມວັນພັກ' }}
        </h5>
        <div class="columns">
          <div class="column is-3">
            <label class="label">ວັນທີ</label>
            <DatePicker
              v-model="form.date"
              type="date"
              format="YYYY-MM-DD"
              value-type="format"
              placeholder="ເລືອກວັນທີ"
              :disabled="!!editingId"
              class="is-fullwidth"
            />
          </div>
          <div class="column is-4">
            <label class="label">ຊື່ວັນພັກ</label>
            <input type="text" class="input" v-model="form.name" placeholder="ເຊັ່ນ: ວັນປີໃໝ່ລາວ" @keyup.enter="saveHoliday">
          </div>
          <div class="column is-2" v-if="editingId">
            <label class="label">ສະຖານະ</label>
            <div class="select is-fullwidth">
              <select v-model="form.is_active">
                <option :value="true">ເປີດໃຊ້</option>
                <option :value="false">ປິດໃຊ້</option>
              </select>
            </div>
          </div>
          <div class="column is-auto" v-if="!editingId">
            <label class="label">&nbsp;</label>
            <label class="checkbox recurring-check">
              <input type="checkbox" v-model="form.recurring">
              ນຳໃຊ້ທຸກປີ ({{ currentYear }}–{{ currentYear + 5 }})
            </label>
          </div>
          <div class="column is-3 form-actions">
            <vs-button @click="saveHoliday" :loading="saving" :disabled="!form.date || !form.name">
              <i class="fal fa-check"></i> {{ editingId ? 'ອັບເດດ' : 'ບັນທຶກ' }}
            </vs-button>
            <vs-button flat @click="cancelForm">ຍົກເລີກ</vs-button>
          </div>
        </div>
      </div>

      <!-- Actions bar -->
      <div class="box actions-bar" v-if="!showForm">
        <div class="columns is-vcentered">
          <div class="column is-3">
            <div class="select is-fullwidth">
              <select v-model="selectedYear" @change="fetchHolidays">
                <option v-for="y in yearOptions" :key="y" :value="y">{{ y }}</option>
              </select>
            </div>
          </div>
          <!-- Add/seed holidays: SuperAdmin/accountingmanager only. UI-only gate — not enforced at Go. -->
          <div v-if="$can(['SuperAdmin', 'accountingmanager'])" class="column action-buttons">
            <vs-button @click="openAddForm">
              <i class="fal fa-plus"></i> ເພີ່ມວັນພັກ
            </vs-button>
            <vs-button flat @click="showSeedConfirm = true" :disabled="seeding">
              <i class="fal fa-calendar-plus"></i> ເພີ່ມວັນພັກເລີ່ມຕົ້ນ
            </vs-button>
          </div>
        </div>
      </div>

      <!-- Loading -->
      <div v-if="isLoading" class="box has-text-centered py-5">
        <i class="fal fa-spinner fa-spin fa-2x"></i>
        <p class="mt-3">ກຳລັງໂຫລດ...</p>
      </div>

      <!-- Error -->
      <div v-else-if="error" class="box notification is-warning">
        <p>{{ error }}</p>
        <p v-if="errorRequestId" class="error-ref">ref: {{ errorRequestId }}</p>
        <vs-button @click="fetchHolidays" class="mt-3">
          <i class="fal fa-redo"></i> ລອງໃໝ່
        </vs-button>
      </div>

      <!-- Table -->
      <div v-else-if="holidays.length > 0" class="box">
        <table class="table is-fullwidth is-hoverable">
          <thead>
            <tr>
              <th>ວັນທີ</th>
              <th>ຊື່</th>
              <th>ສະຖານະ</th>
              <th class="has-text-right">ຈັດການ</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="h in holidays" :key="h.id">
              <td>{{ h.date }}</td>
              <td>{{ h.name }}</td>
              <td>
                <span v-if="h.is_active" class="tag is-success">ເປີດໃຊ້</span>
                <span v-else class="tag is-light">ປິດໃຊ້</span>
              </td>
              <!-- Edit/delete holiday: SuperAdmin/accountingmanager only. UI-only gate — not enforced at Go. -->
              <td class="has-text-right action-cell">
                <vs-button v-if="$can(['SuperAdmin', 'accountingmanager'])" flat icon size="small" @click="editHoliday(h)" title="ແກ້ໄຂ">
                  <i class="fal fa-edit"></i>
                </vs-button>
                <vs-button v-if="$can(['SuperAdmin', 'accountingmanager'])" flat icon size="small" color="danger" @click="confirmDelete(h)" title="ລຶບ">
                  <i class="fal fa-trash"></i>
                </vs-button>
                <span v-if="!$can(['SuperAdmin', 'accountingmanager'])" class="no-data">-</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Empty -->
      <div v-else class="box has-text-centered py-5">
        <i class="fal fa-calendar-star fa-3x has-text-grey-light"></i>
        <p class="mt-3 has-text-grey">ບໍ່ພົບວັນພັກສຳລັບປີ {{ selectedYear }}</p>
        <vs-button v-if="$can(['SuperAdmin', 'accountingmanager'])" class="mt-3" @click="openAddForm">
          <i class="fal fa-plus"></i> ເພີ່ມວັນພັກ
        </vs-button>
      </div>
    </div>

    <!-- Delete confirmation -->
    <vs-dialog v-model="showDeleteConfirm" width="400px" prevent-close>
      <template #header>
        <h4 class="confirm-title"><i class="fal fa-trash"></i> ລຶບວັນພັກ</h4>
      </template>
      <div class="confirm-body">
        <p>ລຶບ <strong>{{ deletingHoliday?.name }}</strong> ({{ deletingHoliday?.date }})?</p>
      </div>
      <template #footer>
        <div class="confirm-footer">
          <vs-button flat @click="showDeleteConfirm = false" :disabled="deleting">ຍົກເລີກ</vs-button>
          <vs-button color="danger" @click="doDelete" :loading="deleting">
            <i class="fal fa-trash"></i> ລຶບ
          </vs-button>
        </div>
      </template>
    </vs-dialog>

    <!-- Seed default holidays confirmation -->
    <vs-dialog v-model="showSeedConfirm" width="500px" prevent-close>
      <template #header>
        <h4 class="confirm-title"><i class="fal fa-calendar-plus"></i> ເພີ່ມວັນພັກເລີ່ມຕົ້ນ</h4>
      </template>
      <div class="confirm-body">
        <p>ເພີ່ມວັນພັກລັດຖະການລາວ 8 ວັນ ສຳລັບ {{ currentYear }}–{{ currentYear + 5 }}:</p>
        <ul class="seed-list">
          <li v-for="h in defaultHolidays" :key="h.month + '-' + h.day">
            {{ h.day }}/{{ h.month }} — {{ h.name }}
          </li>
        </ul>
        <p class="seed-note">ວັນທີ່ມີແລ້ວຈະຖືກຂ້າມ</p>
      </div>
      <template #footer>
        <div class="confirm-footer">
          <vs-button flat @click="showSeedConfirm = false" :disabled="seeding">ຍົກເລີກ</vs-button>
          <vs-button @click="seedDefaultHolidays" :loading="seeding">
            <i class="fal fa-check"></i> ເພີ່ມ
          </vs-button>
        </div>
      </template>
    </vs-dialog>
  </div>
</template>

<script>
import walletService, { extractErrorInfo } from '@/services/walletService';
import { formatErrorMessage } from '@/services/walletFormatters';
import DatePicker from 'vue2-datepicker';
import 'vue2-datepicker/index.css';

export default {
  name: 'Holidays',
  components: { DatePicker },
  data() {
    const now = new Date();
    return {
      holidays: [],
      isLoading: false,
      error: null,
      errorRequestId: null,
      selectedYear: now.getFullYear(),
      calendarMonth: now.getMonth(), // 0-indexed
      calendarYear: now.getFullYear(),

      // Form
      showForm: false,
      editingId: null,
      form: { date: '', name: '', is_active: true, recurring: false },
      saving: false,
      currentYear: now.getFullYear(),

      // Delete
      showDeleteConfirm: false,
      deletingHoliday: null,
      deleting: false,

      // Seed defaults
      showSeedConfirm: false,
      seeding: false,
      defaultHolidays: [
        { month: '01', day: '01', name: 'ວັນປີໃໝ່ສາກົນ' },
        { month: '03', day: '08', name: 'ວັນແມ່ຍິງສາກົນ' },
        { month: '04', day: '14', name: 'ວັນປີໃໝ່ລາວ' },
        { month: '04', day: '15', name: 'ວັນປີໃໝ່ລາວ' },
        { month: '04', day: '16', name: 'ວັນປີໃໝ່ລາວ' },
        { month: '05', day: '01', name: 'ວັນກຳມະກອນສາກົນ' },
        { month: '07', day: '20', name: 'ວັນສ້າງຕັ້ງສະຫະພັນແມ່ຍິງລາວ' },
        { month: '12', day: '02', name: 'ວັນຊາດ' },
      ],

      dayHeaders: ['ຈ', 'ອ', 'ພ', 'ພຫ', 'ສ', 'ສ', 'ອາ'],
    };
  },
  computed: {
    yearOptions() {
      const current = new Date().getFullYear();
      return [current - 1, current, current + 1, current + 2, current + 3, current + 4, current + 5];
    },
    monthNames() {
      return [
        'ມັງກອນ', 'ກຸມພາ', 'ມີນາ', 'ເມສາ', 'ພຶດສະພາ', 'ມິຖຸນາ',
        'ກໍລະກົດ', 'ສິງຫາ', 'ກັນຍາ', 'ຕຸລາ', 'ພະຈິກ', 'ທັນວາ',
      ];
    },
    holidayMap() {
      const map = {};
      for (const h of this.holidays) {
        map[h.date] = h;
      }
      return map;
    },
    calendarCells() {
      const cells = [];
      const firstDay = new Date(this.calendarYear, this.calendarMonth, 1);
      const lastDay = new Date(this.calendarYear, this.calendarMonth + 1, 0);
      // Monday=0 offset
      let startOffset = (firstDay.getDay() + 6) % 7;

      // Empty cells before first day
      for (let i = 0; i < startOffset; i++) {
        cells.push({ day: null, date: null, weekend: false, holiday: null });
      }

      for (let d = 1; d <= lastDay.getDate(); d++) {
        const dt = new Date(this.calendarYear, this.calendarMonth, d);
        const dayOfWeek = dt.getDay(); // 0=Sun, 6=Sat
        const dateStr = this.formatDate(dt);
        cells.push({
          day: d,
          date: dateStr,
          weekend: dayOfWeek === 0 || dayOfWeek === 6,
          holiday: this.holidayMap[dateStr] || null,
          today: dateStr === this.formatDate(new Date()),
        });
      }

      return cells;
    },
  },
  mounted() {
    this.fetchHolidays();
  },
  methods: {
    formatDate(dt) {
      const y = dt.getFullYear();
      const m = String(dt.getMonth() + 1).padStart(2, '0');
      const d = String(dt.getDate()).padStart(2, '0');
      return `${y}-${m}-${d}`;
    },
    prevMonth() {
      if (this.calendarMonth === 0) {
        this.calendarMonth = 11;
        this.calendarYear--;
      } else {
        this.calendarMonth--;
      }
    },
    nextMonth() {
      if (this.calendarMonth === 11) {
        this.calendarMonth = 0;
        this.calendarYear++;
      } else {
        this.calendarMonth++;
      }
    },
    cellClass(cell) {
      return [
        'calendar-cell',
        {
          'calendar-cell--empty': !cell.day,
          'calendar-cell--weekend': cell.weekend,
          'calendar-cell--holiday': cell.holiday,
          'calendar-cell--today': cell.today,
          'calendar-cell--inactive': cell.holiday && !cell.holiday.is_active,
        },
      ];
    },
    onCellClick(cell) {
      // View-only roles can browse the calendar but not add/edit holidays via a cell click.
      if (!this.$can(['SuperAdmin', 'accountingmanager'])) return;
      if (!cell.day) return;
      if (cell.holiday) {
        this.editHoliday(cell.holiday);
      } else {
        this.openAddForm(cell.date);
      }
    },

    // CRUD
    async fetchHolidays() {
      this.isLoading = true;
      this.error = null;
      this.errorRequestId = null;
      try {
        const response = await walletService.listHolidays(this.$axios, {
          year: this.selectedYear,
          page_size: 100,
        });
        this.holidays = response.data || [];
      } catch (err) {
        console.error('Failed to fetch holidays:', err);
        const { message, requestId } = extractErrorInfo(err);
        this.error = message || 'ບໍ່ສາມາດໂຫລດຂໍ້ມູນວັນພັກໄດ້';
        this.errorRequestId = requestId;
      } finally {
        this.isLoading = false;
      }
    },

    openAddForm(dateStr) {
      this.editingId = null;
      this.form = { date: dateStr || '', name: '', is_active: true, recurring: false };
      this.showForm = true;
    },

    editHoliday(h) {
      this.editingId = h.id;
      this.form = { date: h.date, name: h.name, is_active: h.is_active };
      this.showForm = true;
    },

    cancelForm() {
      this.showForm = false;
      this.editingId = null;
      this.form = { date: '', name: '', is_active: true };
    },

    async saveHoliday() {
      if (!this.form.date || !this.form.name) return;
      this.saving = true;
      try {
        if (this.editingId) {
          await walletService.updateHoliday(this.$axios, this.editingId, {
            name: this.form.name,
            is_active: this.form.is_active,
          });
          this.$vs.notification({ color: 'success', title: 'ສຳເລັດ', text: 'ອັບເດດວັນພັກສຳເລັດ' });
        } else if (this.form.recurring) {
          // Bulk create for current year + next 5 years
          const baseDate = new Date(this.form.date);
          const month = String(baseDate.getMonth() + 1).padStart(2, '0');
          const day = String(baseDate.getDate()).padStart(2, '0');
          let created = 0;
          let skipped = 0;

          for (let y = this.currentYear; y <= this.currentYear + 5; y++) {
            const dateStr = `${y}-${month}-${day}`;
            try {
              await walletService.createHoliday(this.$axios, { date: dateStr, name: this.form.name });
              created++;
            } catch (e) {
              // Skip duplicates (409 Conflict)
              if (e.response?.status === 409) {
                skipped++;
              } else {
                throw e;
              }
            }
          }

          const msg = skipped > 0
            ? `ເພີ່ມ ${created} ປີ (ຂ້າມ ${skipped} ປີທີ່ມີແລ້ວ)`
            : `ເພີ່ມ ${created} ປີ (${this.currentYear}–${this.currentYear + 5})`;
          this.$vs.notification({ color: 'success', title: 'ສຳເລັດ', text: msg });
        } else {
          await walletService.createHoliday(this.$axios, {
            date: this.form.date,
            name: this.form.name,
          });
          this.$vs.notification({ color: 'success', title: 'ສຳເລັດ', text: 'ເພີ່ມວັນພັກສຳເລັດ' });
        }
        this.cancelForm();
        await this.fetchHolidays();
      } catch (err) {
        console.error('Failed to save holiday:', err);
        this.$vs.notification({
          color: 'danger',
          title: 'ເກີດຂໍ້ຜິດພາດ',
          text: formatErrorMessage(err, 'ບໍ່ສາມາດບັນທຶກວັນພັກໄດ້'),
        });
      } finally {
        this.saving = false;
      }
    },

    async seedDefaultHolidays() {
      this.seeding = true;
      let created = 0;
      let skipped = 0;
      try {
        for (let y = this.currentYear; y <= this.currentYear + 5; y++) {
          for (const h of this.defaultHolidays) {
            const dateStr = `${y}-${h.month}-${h.day}`;
            try {
              await walletService.createHoliday(this.$axios, { date: dateStr, name: h.name });
              created++;
            } catch (e) {
              if (e.response?.status === 409) {
                skipped++;
              } else {
                throw e;
              }
            }
          }
        }
        this.$vs.notification({
          color: 'success',
          title: 'ສຳເລັດ',
          text: `ເພີ່ມ ${created} ວັນພັກ (ຂ້າມ ${skipped} ວັນທີ່ມີແລ້ວ)`,
        });
        this.showSeedConfirm = false;
        await this.fetchHolidays();
      } catch (err) {
        console.error('Failed to seed holidays:', err);
        this.$vs.notification({
          color: 'danger',
          title: 'ເກີດຂໍ້ຜິດພາດ',
          text: formatErrorMessage(err, 'ບໍ່ສາມາດເພີ່ມວັນພັກເລີ່ມຕົ້ນໄດ້') + ` (ເພີ່ມແລ້ວ ${created})`,
        });
      } finally {
        this.seeding = false;
      }
    },

    confirmDelete(h) {
      this.deletingHoliday = h;
      this.showDeleteConfirm = true;
    },

    async doDelete() {
      if (!this.deletingHoliday) return;
      this.deleting = true;
      try {
        await walletService.deleteHoliday(this.$axios, this.deletingHoliday.id);
        this.$vs.notification({ color: 'success', title: 'ສຳເລັດ', text: 'ລຶບວັນພັກສຳເລັດ' });
        this.showDeleteConfirm = false;
        this.deletingHoliday = null;
        await this.fetchHolidays();
      } catch (err) {
        console.error('Failed to delete holiday:', err);
        this.$vs.notification({
          color: 'danger',
          title: 'ເກີດຂໍ້ຜິດພາດ',
          text: formatErrorMessage(err, 'ບໍ່ສາມາດລຶບວັນພັກໄດ້'),
        });
      } finally {
        this.deleting = false;
      }
    },
  },
  watch: {
    selectedYear() {
      this.calendarYear = this.selectedYear;
      this.calendarMonth = 0;
    },
    calendarYear(newYear) {
      if (newYear !== this.selectedYear) {
        this.selectedYear = newYear;
        this.fetchHolidays();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.box-title {
  margin-bottom: 1rem;
  h4 { font-size: 1.25rem; font-weight: 600; }
}

.countTotal {
  font-weight: normal;
  color: #888;
  font-size: 0.9rem;
}

.py-5 { padding: 3rem 0; }
.mt-3 { margin-top: 1rem; }
.error-ref { font-size: 12px; color: #856404; margin-top: 4px; }

// Calendar
.calendar-header {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  margin-bottom: 12px;
}

.calendar-selectors {
  display: flex;
  gap: 8px;
  align-items: center;
}

.calendar-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 2px;
  margin-bottom: 12px;
}

.calendar-day-header {
  text-align: center;
  font-weight: 600;
  font-size: 0.85rem;
  padding: 8px 0;
  color: #666;
}

.calendar-cell {
  text-align: center;
  padding: 8px 4px;
  min-height: 44px;
  border-radius: 6px;
  cursor: pointer;
  position: relative;
  transition: background 0.15s;

  &:hover:not(.calendar-cell--empty) {
    background: #e8e8e8;
  }

  &--empty {
    cursor: default;
  }

  &--weekend {
    background: #f0eff4;
    .day-number { color: #999; }
  }

  &--holiday {
    background: #d4edda;
    font-weight: 600;
    &:hover { background: #b8dfc5; }
  }

  &--holiday.calendar-cell--inactive {
    background: #fff3cd;
    &:hover { background: #ffe0a0; }
  }

  &--today {
    .day-number {
      background: #3273dc;
      color: #fff;
      border-radius: 50%;
      display: inline-block;
      width: 28px;
      height: 28px;
      line-height: 28px;
    }
  }
}

.day-number {
  font-size: 0.9rem;
}

.holiday-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #28a745;
  position: absolute;
  bottom: 4px;
  left: 50%;
  transform: translateX(-50%);
}

.calendar-legend {
  display: flex;
  gap: 24px;
  justify-content: center;
  font-size: 0.85rem;
  color: #555;
  padding: 4px 0;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 8px;

  .holiday-dot {
    position: static;
    transform: none;
  }
}

.weekend-dot {
  display: inline-block;
  width: 14px;
  height: 14px;
  border-radius: 3px;
  background: #f0eff4;
  border: 1px solid #ccc;
}

.action-buttons {
  display: flex;
  gap: 8px;
  align-items: center;
}

.seed-list {
  text-align: left;
  margin: 12px auto;
  max-width: 300px;
  list-style: none;
  padding: 0;

  li {
    padding: 4px 0;
    font-size: 0.9rem;
    border-bottom: 1px solid #f0f0f0;
  }
}

.seed-note {
  font-size: 0.85rem;
  color: #888;
  font-style: italic;
}

// Action buttons inline
.action-cell {
  white-space: nowrap;
  .vs-button { display: inline-flex; }
}

// Form
.form-box {
  border-left: 4px solid #3273dc;
}

.form-title {
  font-size: 1rem;
  font-weight: 600;
  margin-bottom: 12px;
  i { margin-right: 6px; color: #3273dc; }
}

.form-actions {
  display: flex;
  align-items: flex-end;
  gap: 8px;
  padding-bottom: 4px;
}

.recurring-check {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.9rem;
  color: #555;
  padding-top: 8px;

  input[type="checkbox"] {
    width: 16px;
    height: 16px;
  }
}

.actions-bar {
  padding: 12px 20px;
}

// Confirm dialog
.confirm-title {
  font-size: 1.1rem;
  font-weight: 600;
  i { margin-right: 8px; color: #f14668; }
}

.confirm-body {
  padding: 20px;
  text-align: center;
  p { margin-bottom: 8px; font-size: 1rem; }
}

.confirm-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 16px 20px;
  border-top: 1px solid #eee;
}
</style>
